import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { categories, movies } from "@/mocks/movies";
import MovieCard from "@/components/feature/MovieCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import Pagination from "@/components/feature/Pagination";
import { usePageMeta } from "@/hooks/usePageMeta";

const PAGE_SIZE = 4;
const ALL = "全部";

const sortOptions = [
  { key: "default", label: "综合排序" },
  { key: "new", label: "最新上线" },
  { key: "rating", label: "评分最高" },
];

interface FilterGroupProps {
  label: string;
  options: string[];
  value: string;
  onChange: (value: string) => void;
}

function FilterGroup({ label, options, value, onChange }: FilterGroupProps) {
  return (
    <div>
      <h3 className="text-xs font-bold text-foreground-500 uppercase tracking-wide mb-2.5">{label}</h3>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => onChange(option)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
              value === option
                ? "bg-primary-500 text-background-50"
                : "bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-700"
            }`}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function CategoryPage() {
  const { type } = useParams();
  const category = categories.find((item) => item.key === type);

  const list = useMemo(
    () => movies.filter((movie) => movie.type === (category?.key ?? "")),
    [category],
  );

  const regions = useMemo(
    () => [ALL, ...Array.from(new Set(list.map((m) => m.region)))],
    [list],
  );
  const years = useMemo(
    () => [ALL, ...Array.from(new Set(list.map((m) => String(m.year)))).sort((a, b) => Number(b) - Number(a))],
    [list],
  );
  const genres = useMemo(
    () => [ALL, ...Array.from(new Set(list.flatMap((m) => m.genres)))],
    [list],
  );

  const [region, setRegion] = useState(ALL);
  const [year, setYear] = useState(ALL);
  const [genre, setGenre] = useState(ALL);
  const [sort, setSort] = useState("default");
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    setRegion(ALL);
    setYear(ALL);
    setGenre(ALL);
    setSort("default");
    setPage(1);
    setShowFilters(false);
  }, [type]);

  const filtered = useMemo(() => {
    const result = list.filter(
      (movie) =>
        (region === ALL || movie.region === region) &&
        (year === ALL || String(movie.year) === year) &&
        (genre === ALL || movie.genres.includes(genre)),
    );
    if (sort === "new") return [...result].sort((a, b) => b.year - a.year || b.rating - a.rating);
    return [...result].sort((a, b) => b.rating - a.rating);
  }, [list, region, year, genre, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  usePageMeta(
    category ? `${category.title}分类 - 蜜月TV` : "分类 - 蜜月TV",
    category ? category.description : "浏览蜜月TV精选影视内容。",
  );

  const resetFilters = () => {
    setRegion(ALL);
    setYear(ALL);
    setGenre(ALL);
    setSort("default");
    setPage(1);
    setShowFilters(false);
  };

  if (!category) {
    return (
      <div className="w-full px-4 md:px-8 py-20 text-center">
        <i className="ri-emotion-sad-line text-5xl text-foreground-300" />
        <h1 className="text-xl font-bold text-foreground-900 mt-4">分类不存在</h1>
        <p className="text-sm text-foreground-500 mt-2">你访问的分类可能已被移除或输入有误。</p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const filterPanel = (
    <div className="space-y-6">
      <FilterGroup label="地区" options={regions} value={region} onChange={(v) => { setRegion(v); setPage(1); }} />
      <FilterGroup label="年份" options={years} value={year} onChange={(v) => { setYear(v); setPage(1); }} />
      <FilterGroup label="类型" options={genres} value={genre} onChange={(v) => { setGenre(v); setPage(1); }} />
      <FilterGroup
        label="排序"
        options={sortOptions.map((s) => s.label)}
        value={sortOptions.find((s) => s.key === sort)?.label ?? "综合排序"}
        onChange={(v) => {
          const found = sortOptions.find((s) => s.label === v);
          setSort(found ? found.key : "default");
          setPage(1);
        }}
      />
      <button
        type="button"
        onClick={resetFilters}
        className="w-full flex items-center justify-center gap-1.5 px-4 py-2 rounded-md border border-background-200 text-sm text-foreground-600 hover:border-primary-300 hover:text-primary-700 transition-colors cursor-pointer"
      >
        <i className="ri-refresh-line" /> 重置筛选
      </button>
    </div>
  );

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-8">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: `${category.title}分类` },
        ]}
      />

      <header className="mt-4">
        <h1 className="text-2xl md:text-3xl font-black text-foreground-950">
          {category.subtitle}
        </h1>
        <p className="text-sm text-foreground-500 mt-2">{category.description}</p>
      </header>

      <div className="flex flex-col lg:flex-row gap-8 mt-6">
        <aside className="w-full lg:w-60 shrink-0">
          <button
            type="button"
            onClick={() => setShowFilters((v) => !v)}
            className="lg:hidden w-full flex items-center justify-between px-4 py-3 rounded-lg bg-background-100 border border-background-200 text-sm font-medium text-foreground-800 cursor-pointer"
          >
            <span className="flex items-center gap-2">
              <i className="ri-filter-3-line" /> 筛选条件
            </span>
            <i className={showFilters ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
          </button>
          <div
            className={`${showFilters ? "block" : "hidden"} lg:block mt-4 lg:mt-0 p-5 rounded-lg bg-background-50 border border-background-200`}
          >
            {filterPanel}
          </div>
        </aside>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-foreground-500">
              共 <span className="font-bold text-primary-700">{filtered.length}</span> 部作品
            </p>
          </div>

          {paged.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center rounded-lg bg-background-50 border border-dashed border-background-300">
              <i className="ri-inbox-line text-5xl text-foreground-300" />
              <h2 className="text-base font-bold text-foreground-800 mt-4">没有找到匹配的内容</h2>
              <p className="text-sm text-foreground-500 mt-2">试试调整筛选条件，或重置后重新浏览。</p>
              <button
                type="button"
                onClick={resetFilters}
                className="mt-5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                重置筛选条件
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {paged.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
              <Pagination current={currentPage} totalPages={totalPages} onChange={setPage} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}